















































































































































import _ from "lodash";
import { Component, Prop, PropSync, Vue, Watch } from "vue-property-decorator";
import rest from "@/rest";
// interfaces
import { ParamDictionary } from "@/interfaces/ParamDictionary";
import { TrainingBundleModuleVm } from "@/interfaces/TrainingBundleModuleVm";
import { TrainingBundleSuggestionVm } from "@/interfaces/TrainingBundleSuggestionVm";
import { TrainingELearningPhase, TrainingEventExBundleVm } from "@/interfaces/TrainingEventExBundleVm";

interface ReleaseRow {
  key: number;
  id: number;
  moduleGuid: string | null;
  isMissing: boolean;
  afterSessionOrder: string | null;
}

// Adds or edits an e-learning of an event. Emits "saved" with the saved entry.
@Component
export default class TrainingEventExBundleEditDialog extends Vue {
  @PropSync("showDialog") syncedShowDialog!: boolean;
  @Prop({ required: true, type: Number }) readonly orgId!: number;
  @Prop({ required: true, type: Number }) readonly eventId!: number;
  @Prop({ default: null }) readonly entry!: TrainingEventExBundleVm | null;

  readonly TrainingELearningPhase = TrainingELearningPhase;
  readonly phaseItems = [
    { text: "Vorbereitung (vor dem Termin)", value: TrainingELearningPhase.Preparation },
    { text: "Nachbereitung (nach dem Termin)", value: TrainingELearningPhase.FollowUp }
  ];

  valid = true;
  saving = false;
  nextKey = 1;

  selectedBundle: TrainingBundleSuggestionVm | null = null;
  bundleSuggestions: TrainingBundleSuggestionVm[] = [];
  bundleSearch = "";
  loadingBundles = false;

  modules: TrainingBundleModuleVm[] = [];
  loadingModules = false;

  phase: TrainingELearningPhase = TrainingELearningPhase.Preparation;
  isCompletionRelevant = true;
  assignmentOffsetDays: string | null = "";
  deadlineOffsetDays: string | null = "";
  absoluteAssignmentDate = "";
  absoluteDeadline = "";
  releases: ReleaseRow[] = [];

  readonly debouncedSearch = _.debounce(() => this.loadBundles(), 300);
  readonly offsetRules = [(v: string | null) => v === null || v === "" || (/^-?\d+$/.test(String(v)) && Math.abs(Number(v)) <= 365) || "Ganze Zahl von -365 bis 365."];
  readonly sessionRules = [(v: string | null) => v === null || v === "" || (/^\d+$/.test(String(v)) && Number(v) >= 1) || "Ganze Zahl ab 1."];

  get isSequential(): boolean {
    return this.entry ? this.entry.isSequential : !!this.selectedBundle?.isSequential;
  }

  get bundleItems(): TrainingBundleSuggestionVm[] {
    return _.uniqBy([...(this.selectedBundle ? [this.selectedBundle] : []), ...this.bundleSuggestions], b => b.id);
  }

  @Watch("syncedShowDialog")
  async onShowDialogChanged(show: boolean): Promise<void> {
    if (!show) return;
    this.reset();
    if (this.entry) await this.loadModules(this.entry.exBundleId);
    else await this.loadBundles();
  }

  @Watch("bundleSearch")
  onBundleSearchChanged(search: string | null): void {
    if (this.selectedBundle && this.selectedBundle.name === search) return;
    this.debouncedSearch();
  }

  reset(): void {
    const e = this.entry;
    this.selectedBundle = null;
    this.bundleSearch = "";
    this.modules = [];
    this.phase = e?.phase ?? TrainingELearningPhase.Preparation;
    this.isCompletionRelevant = e?.isCompletionRelevant ?? true;
    this.assignmentOffsetDays = e && e.assignmentOffsetDays !== null ? String(e.assignmentOffsetDays) : "";
    this.deadlineOffsetDays = e && e.deadlineOffsetDays !== null ? String(e.deadlineOffsetDays) : "";
    this.absoluteAssignmentDate = this.$globalHelper.toDateTimeInputValue(e?.absoluteAssignmentDate);
    this.absoluteDeadline = this.$globalHelper.toDateTimeInputValue(e?.absoluteDeadline);
    this.releases = (e?.moduleReleases || []).map(r => ({
      key: this.nextKey++,
      id: r.id,
      moduleGuid: r.moduleGuid,
      isMissing: r.isMissing,
      afterSessionOrder: r.afterSessionOrder === null ? "" : String(r.afterSessionOrder)
    }));
    this.$nextTick(() => (this.$refs.form as any)?.resetValidation());
  }

  async loadBundles(): Promise<void> {
    this.loadingBundles = true;
    try {
      const params: ParamDictionary = { dictionary: {} };
      params.dictionary!["OrgId"] = this.orgId.toString();
      params.dictionary!["Search"] = this.bundleSearch || "";
      // On errors the rest wrapper shows the message and returns undefined
      this.bundleSuggestions = (await rest.url("trainingLead/searchBundles").post(params)) ?? [];
    } finally {
      this.loadingBundles = false;
    }
  }

  async onBundleSelected(bundle: TrainingBundleSuggestionVm | null): Promise<void> {
    this.releases = [];
    this.modules = [];
    if (bundle && bundle.isSequential) await this.loadModules(bundle.id);
  }

  async loadModules(bundleId: number): Promise<void> {
    this.loadingModules = true;
    try {
      const params: ParamDictionary = { dictionary: {} };
      params.dictionary!["ExBundleId"] = bundleId.toString();
      this.modules = (await rest.url("trainingLead/getBundleModules").post(params)) ?? [];
    } finally {
      this.loadingModules = false;
    }
  }

  // Modules that are not used by another release (and the one of this row, even if it no longer exists)
  moduleItems(release: ReleaseRow): TrainingBundleModuleVm[] {
    const used = this.releases.filter(r => r.key !== release.key).map(r => r.moduleGuid);
    const items = this.modules.filter(m => !used.includes(m.guid));
    if (release.isMissing && release.moduleGuid) {
      const original = this.entry?.moduleReleases?.find(r => r.moduleGuid === release.moduleGuid);
      items.push({ guid: release.moduleGuid, name: (original?.moduleName || "Unbekanntes Modul") + " (fehlt)" });
    }
    return items;
  }

  addRelease(): void {
    this.releases.push({ key: this.nextKey++, id: 0, moduleGuid: null, isMissing: false, afterSessionOrder: "" });
  }

  close(): void {
    this.syncedShowDialog = false;
  }

  toDays(value: string | null): number | null {
    return value === null || value === "" ? null : Number(value);
  }

  async save(): Promise<void> {
    if (!(this.$refs.form as any).validate()) return;
    const bundleId = this.entry ? this.entry.exBundleId : this.selectedBundle?.id;
    if (!bundleId) return;

    this.saving = true;
    try {
      const vm: TrainingEventExBundleVm = {
        id: this.entry?.id ?? 0,
        trainingEventId: this.eventId,
        exBundleId: bundleId,
        exBundleName: null,
        isSequential: this.isSequential,
        phase: this.phase,
        order: this.entry?.order ?? 0,
        isCompletionRelevant: this.isCompletionRelevant,
        assignmentOffsetDays: this.toDays(this.assignmentOffsetDays),
        deadlineOffsetDays: this.toDays(this.deadlineOffsetDays),
        absoluteAssignmentDate: this.$globalHelper.fromDateTimeInputValue(this.absoluteAssignmentDate) as any,
        absoluteDeadline: this.$globalHelper.fromDateTimeInputValue(this.absoluteDeadline) as any,
        moduleReleases: this.releases.map(r => ({
          id: r.id,
          moduleGuid: r.moduleGuid,
          moduleName: null,
          isMissing: false,
          afterSessionOrder: this.toDays(r.afterSessionOrder)
        }))
      };

      // On errors the rest wrapper shows the message and returns undefined: keep the dialog open then
      const result: TrainingEventExBundleVm | undefined = await rest.url("trainingLead/saveEventExBundle").post(vm);
      if (!result) return;
      this.syncedShowDialog = false;
      this.$emit("saved", result);
    } finally {
      this.saving = false;
    }
  }
}
